/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class CompleteTheLookItem extends HTMLElement {
  connectedCallback() {
    this._variantInput = this.querySelector('[data-ctl-variant-input]');
    this._submitButton = this.querySelector('[data-ctl-submit]') || this.querySelector('.complete-the-look__submit[type="submit"]');
    this._variantSelect = this.querySelector('[data-ctl-variant-select]');
    this._colourElement = this.querySelector('[data-ctl-colour]');
    this._colorOptionIndex = Number.isInteger(parseInt(this.dataset.colorOptionIndex, 10))
      ? parseInt(this.dataset.colorOptionIndex, 10)
      : null;

    if (this._variantSelect) {
      const soldOutText = this.dataset.soldOutText || 'Sold out';

      if (this._submitButton && !this._submitButton.dataset.availableText) {
        this._submitButton.dataset.availableText = this._getSubmitButtonLabel();
        this._submitButton.dataset.soldOutText = soldOutText;
      }

      this._variantSelect.addEventListener('change', () => this._syncFromVariantId(this._variantSelect.value));
      return;
    }

    const sizeSelect = this.querySelector('[data-ctl-size-select]');
    if (!sizeSelect || !this._variantInput) return;

    const variants = JSON.parse(this.querySelector('[data-ctl-variants]')?.textContent || '[]');
    const sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
    const defaultVariantId = parseInt(this.dataset.defaultVariantId, 10);
    const defaultVariant = variants.find((variant) => variant.id === defaultVariantId);
    const soldOutText = this.dataset.soldOutText || 'Sold out';

    const resolveVariantForSize = (sizeValue) => {
      if (!variants.length) return null;

      if (defaultVariant) {
        const exactMatch = variants.find(
          (variant) =>
            variant.available &&
            variant.options[sizeOptionIndex] === sizeValue &&
            variant.options.every((option, index) => index === sizeOptionIndex || option === defaultVariant.options[index])
        );
        if (exactMatch) return exactMatch;
      }

      return variants.find((variant) => variant.available && variant.options[sizeOptionIndex] === sizeValue) || null;
    };

    const clearSelection = () => {
      if (this._variantInput) {
        this._variantInput.value = '';
      }

      if (this._submitButton) {
        this._submitButton.classList.add('complete-the-look__submit--awaiting-size');
        this._submitButton.disabled = true;
        this._setSubmitButtonLabel(this._submitButton.dataset.availableText || this._getSubmitButtonLabel());
      }
    };

    const applyVariant = (variant) => {
      if (!variant || !this._variantInput) return;

      this._variantInput.value = variant.id;
      this._updateColourFromVariant(variant);

      if (this._submitButton) {
        this._submitButton.classList.remove('complete-the-look__submit--awaiting-size');
        this._submitButton.disabled = !variant.available;
        this._setSubmitButtonLabel(
          variant.available
            ? this._submitButton.dataset.availableText || this._getSubmitButtonLabel()
            : this._submitButton.dataset.soldOutText || soldOutText
        );
      }
    };

    if (this._submitButton && !this._submitButton.dataset.availableText) {
      this._submitButton.dataset.availableText = this._getSubmitButtonLabel();
      this._submitButton.dataset.soldOutText = soldOutText;
    }

    sizeSelect.addEventListener('change', () => {
      if (!sizeSelect.value) {
        clearSelection();
        return;
      }

      const variant = resolveVariantForSize(sizeSelect.value);
      if (!variant) {
        clearSelection();
        return;
      }

      applyVariant(variant);
    });

    clearSelection();
  }

  _getSubmitButtonLabel() {
    if (!this._submitButton) return '';

    if (this._submitButton.textElement) {
      return this._submitButton.textElement.textContent.trim();
    }

    const textElement = this._submitButton.querySelector('.loader-button__text');
    if (textElement) {
      return textElement.textContent.trim();
    }

    return this._submitButton.textContent.trim();
  }

  _setSubmitButtonLabel(text) {
    if (!this._submitButton) return;

    if (this._submitButton.textElement) {
      this._submitButton.textElement.textContent = text;
      return;
    }

    const textElement = this._submitButton.querySelector('.loader-button__text');
    if (textElement) {
      textElement.textContent = text;
      return;
    }

    this._submitButton.textContent = text;
  }

  _syncFromVariantId(variantId) {
    if (!this._variantInput) return;

    this._variantInput.value = variantId;

    const variants = JSON.parse(this.querySelector('[data-ctl-variants]')?.textContent || '[]');
    const variant = variants.find((entry) => String(entry.id) === String(variantId));
    if (variant) {
      this._updateColourFromVariant(variant);
    }

    if (!this._submitButton || !this._variantSelect) return;

    const selectedOption = this._variantSelect.selectedOptions[0];
    const isAvailable = selectedOption && !selectedOption.disabled;

    this._submitButton.disabled = !isAvailable;
    this._setSubmitButtonLabel(
      isAvailable
        ? this._submitButton.dataset.availableText || this._getSubmitButtonLabel()
        : this._submitButton.dataset.soldOutText || this.dataset.soldOutText || 'Sold out'
    );
  }

  _updateColourFromVariant(variant) {
    if (!this._colourElement || this._colorOptionIndex === null || !variant?.options) return;

    const colour = variant.options[this._colorOptionIndex];
    if (!colour) return;

    this._colourElement.textContent = `Colour: ${colour}`;
  }
}

if (!window.customElements.get('complete-the-look-item')) {
  window.customElements.define('complete-the-look-item', CompleteTheLookItem);
}

class ProductTabsAccordion extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const answerPaddingTop = 10;
    const answerPaddingBottom = 20;
    const closeOther = this.dataset.closeOther === 'true';
    const openDefault = this.dataset.openDefault === 'true';

    const faqs = Array.from(this.querySelectorAll('.product-tabs__faq:not(.product-tabs__faq--measure)'));
    const buttons = faqs.map((faq) => faq.querySelector('.product-tabs__button'));
    const answers = faqs.map((faq) => faq.querySelector('.product-tabs__answer'));
    const measureAnswers = Array.from(this.querySelectorAll('.product-tabs__faq--measure .product-tabs__answer--measure'));
    const arrows = faqs.map((faq) => faq.querySelector('.product-tabs__arrow'));

    let activeIndex = -1;

    const isAnswerOpen = (index) => faqs[index]?.classList.contains('is-open');

    const openAnswer = (index) => {
      const height = measureAnswers[index]?.offsetHeight || 0;
      const answer = answers[index];
      const arrow = arrows[index];
      const faq = faqs[index];
      if (!answer || !faq) return;

      faq.classList.add('is-open');
      answer.style.display = 'block';
      requestAnimationFrame(() => {
        answer.style.paddingTop = `${answerPaddingTop}px`;
        answer.style.paddingBottom = `${answerPaddingBottom}px`;
        answer.style.height = `${height}px`;
        if (arrow) arrow.style.setProperty('--product-tabs-rotate', '180deg');
      });
    };

    const closeAnswer = (index) => {
      const answer = answers[index];
      const arrow = arrows[index];
      const faq = faqs[index];
      if (!answer || !faq) return;

      faq.classList.remove('is-open');
      answer.style.paddingTop = '0';
      answer.style.paddingBottom = '0';
      answer.style.height = '0';
      if (arrow) arrow.style.setProperty('--product-tabs-rotate', '0deg');

      window.setTimeout(() => {
        if (!faq.classList.contains('is-open')) {
          answer.style.display = 'none';
        }
      }, 300);
    };

    const toggleAnswer = (index) => {
      if (!answers[index]) return;

      if (isAnswerOpen(index)) {
        closeAnswer(index);
        activeIndex = -1;
      } else {
        openAnswer(index);
        activeIndex = index;
      }
    };

    const closeAllAnswersBut = (index) => {
      faqs.forEach((faq, i) => {
        if (i !== index && isAnswerOpen(i)) {
          closeAnswer(i);
        }
      });
    };

    buttons.forEach((button, index) => {
      if (!button) return;
      button.addEventListener('click', () => {
        const isClosed = !isAnswerOpen(index);

        if (closeOther && isClosed) {
          closeAllAnswersBut(index);
        }

        toggleAnswer(index);
      });
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (activeIndex !== -1 && measureAnswers[activeIndex]) {
          answers[activeIndex].style.height = `${measureAnswers[activeIndex].offsetHeight}px`;
        }
      }, 250);
    });

    if (openDefault && faqs.length > 0) {
      openAnswer(0);
      activeIndex = 0;
    }
  }
}

if (!window.customElements.get('product-tabs-accordion')) {
  window.customElements.define('product-tabs-accordion', ProductTabsAccordion);
}

class AnnouncementCountdown extends HTMLElement {
  connectedCallback() {
    this.endTime = Date.parse(this.dataset.end || '');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');

    if (!Number.isFinite(this.endTime) || !this.hoursEl || !this.minutesEl || !this.secondsEl) {
      this.hidden = true;
      return;
    }

    this.tick();
    this.intervalId = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
    }
  }

  tick() {
    const remaining = Math.max(0, this.endTime - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.hoursEl.textContent = String(hours).padStart(2, '0');
    this.minutesEl.textContent = String(minutes).padStart(2, '0');
    this.secondsEl.textContent = String(seconds).padStart(2, '0');

    if (remaining === 0) {
      this.hidden = true;
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
      }
    }
  }
}

if (!window.customElements.get('announcement-countdown')) {
  window.customElements.define('announcement-countdown', AnnouncementCountdown);
}